import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import { SupportedFileInput } from '@uploadcare/upload-client';
import { startCase } from 'lodash';
import {
  useCreateNote,
  useDeleteNote,
  useGetNotes,
  useUpdateNote,
} from 'modules/account/contact/hooks';
import { useGetSubscription } from 'modules/account/settings/hooks';
import { Button, DeleteItemModal, Entry } from 'ui/components/common';
import { ENTRY_TYPE } from 'ui/components/common/Entry/Entry';
import { Composer } from 'ui/partials';
import useModal from 'ui/partials/Modal/useModal';
import { COMPOSER_TYPES } from 'utils/constants';
import { useUploadToUploadCare } from 'utils/helpers';
import { Comment } from 'utils/types';

interface CONTEXT {
  selectedNoteId: string | null;
  setSelectedNoteId: Dispatch<SetStateAction<string | null>>;
}

export const CompanyNotesContext = createContext<CONTEXT | null>(null);
// eslint-disable-next-line react-refresh/only-export-components
export const useCompanyNotesContext = () => useContext(CompanyNotesContext);

interface PROPS {
  id: string;
  source: string;
}

const Notes: FC<PROPS> = ({ id, source }) => {
  const [openNotesComposer, setOpenNotesComposer] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const { showModal } = useModal();
  const { mutate: addNote, isLoading: createNoteLoading } = useCreateNote();
  const { mutate: deleteNote, isLoading: deleteLoading } = useDeleteNote();
  const { mutate: updateNote } = useUpdateNote();
  const { mutateAsync: uploadFiles, isLoading: attachmentLoading } =
    useUploadToUploadCare();

  const query = {
    referenceId: id,
    'include[0]': 'comments',
    'include[1]': 'attachments',
    'include[2]': 'accountUser',
  };

  const { data: subscription } = useGetSubscription();
  const { data: notes } = useGetNotes(query);

  const closeComposer = () => {
    setOpenNotesComposer(false);
    setSelectedNoteId(null);
  };

  const onSubmit = async (data: {
    body: string;
    attachments: SupportedFileInput[];
  }) => {
    const uploadedAttachments =
      data?.attachments.length && (await uploadFiles(data.attachments));

    addNote({
      content: data.body,
      referenceId: id,
      source: startCase(source),
      ...(uploadedAttachments && {
        attachments: uploadedAttachments.files.map((file) => ({
          fileUrl: file.cdnUrl,
          fileType: file.mimeType,
          fileName: file.originalFilename,
          fileSize: file.size,
          source: startCase(source),
          ...(file.isImage && { fileThumbnail: file.cdnUrl }),
        })),
      }),
    } as Comment);
  };

  const handleDelete = (noteId: string) => {
    deleteNote({ noteId, id: noteId, referenceId: id });
  };

  const showDeleteModal = (noteId: string) => {
    showModal({
      title: 'Notes',
      disableOutsideClick: true,
      content: (
        <DeleteItemModal
          onClickDelete={() => handleDelete(noteId)}
          isLoading={deleteLoading}
          validation
        />
      ),
    });
  };

  const handleUpdateNote = (
    _id: string,
    value: Comment,
    referenceId: string,
  ) => {
    updateNote({ id: _id, payload: value, referenceId });
  };

  const handleOpenNewNote = () => {
    setOpenNotesComposer(true);
    setSelectedNoteId('NEW-NOTE');
  };

  const value = useMemo(
    () => ({ selectedNoteId, setSelectedNoteId }),
    [selectedNoteId],
  );

  return (
    <CompanyNotesContext.Provider value={value}>
      <div className="mb-5">
        <Button
          onClick={handleOpenNewNote}
          label="CREATE NOTE"
          size="sm"
          disabled={!!selectedNoteId || subscription?.isExpired}
          expiredSubscription={subscription?.isExpired}
          tooltipPosition="right"
        />
      </div>
      <div className="max-h-[calc(100vh-299px)] overflow-y-auto">
        {!notes?.length ? (
          <p className="mb-[14px] text-sm leading-5">
            Take notes about this record to keep track of important info.
          </p>
        ) : (
          notes?.map((entry, index) => (
            <Entry
              onDelete={() => showDeleteModal(entry.id)}
              onEdit={handleUpdateNote}
              open={index === 0}
              key={entry?.id}
              data={entry}
              type={ENTRY_TYPE.NOTES}
            />
          ))
        )}
      </div>
      <Composer
        type={COMPOSER_TYPES.note}
        onSubmit={onSubmit}
        closeComposer={closeComposer}
        isOpen={openNotesComposer}
        isLoading={createNoteLoading || attachmentLoading}
      />
    </CompanyNotesContext.Provider>
  );
};

export default Notes;
